import { observer } from "mobx-react";
import { Fragment, ReactNode, useContext, useEffect, useState } from "react";
import { CommandCentreVenue } from "../state/UserState";
import { UserStore } from "..";
import { useNavigate } from "react-router";
import ModernNavbar, {
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_MOBILE,
} from "../components/ModernNavbar";
import { PEProgressBar } from "../components/linear-progress";
import { isProductionUrl } from "../utilities";
import { useMediaQuery } from "@mui/material";
import { ConfirmDialog } from "../components/confirm-dialog";

const DashboardLayout = observer((props: { children: ReactNode }) => {
  const navigate = useNavigate();
  const userStore = useContext(UserStore);
  // Mobile collapses the second nav row into the drawer, so the header is shorter.
  const isMobile = useMediaQuery("(max-width:899.95px)");
  const headerHeight = isMobile ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP;
  const bannerHeight = 51;

  const { selectedVenue, userVenues, userVenuesAlphabetical, venueSearchTerm } =
    userStore;

  // Venue awaiting confirmation because the current page has unsaved work.
  const [pendingVenue, setPendingVenue] = useState<CommandCentreVenue | null>(
    null
  );

  const selectVenue = (venue: CommandCentreVenue) => {
    if (venue.venueId === selectedVenue?.venueId) return;

    if (userStore.unsavedWork) {
      setPendingVenue(venue);
      return;
    }

    // Shows the venue-switch loading state, then remounts the current page.
    userStore.changeVenueWithLoading(venue);
  };

  const confirmVenueSwitch = () => {
    if (pendingVenue) userStore.changeVenueWithLoading(pendingVenue);
    setPendingVenue(null);
  };

  const updateSearchTerm = (venueSearchTerm: string) => {
    userStore.venueSearchTerm = venueSearchTerm;
  };

  useEffect(() => {
    userStore.refreshExtras();
  }, []);

  if (!userStore.isLoggedIn) {
    navigate("/login");
  }

  return (
    <Fragment>
      <ModernNavbar
        selectedVenue={selectedVenue}
        userVenues={userVenuesAlphabetical}
        totalVenueCount={userVenues?.length ?? 0}
        selectVenue={selectVenue}
        venueSearchTerm={venueSearchTerm}
        updateVenueSearchTerm={updateSearchTerm}
        isSingleVenue={userVenues === undefined || userVenues?.length <= 1}
      />

      <ConfirmDialog
        open={pendingVenue !== null}
        title="Discard unsaved changes?"
        message={`You have ${userStore.unsavedWork ?? "unsaved changes"} on this page. Switching to ${
          pendingVenue?.displayName ?? "another venue"
        } will discard them.`}
        confirmLabel="Discard and switch"
        destructive
        onConfirm={confirmVenueSwitch}
        onCancel={() => setPendingVenue(null)}
      />

      {/* Developer warning banner - only show if not production */}
      {!isProductionUrl() && (
        <div
          style={{
            textAlign: "center",
            color: "#dc2626", // red-700 equivalent
            fontWeight: "800", // font-extrabold equivalent
            fontSize: "1.25rem", // text-xl equivalent
            padding: "0.75rem 0", // py-3 equivalent
            backgroundColor: "#fef2f2", // light red background
            borderBottom: "2px solid #dc2626",
            position: "fixed",
            top: `${headerHeight}px`, // below the fixed navbar
            left: 0,
            right: 0,
            zIndex: 1200, // below navbar but above content
            width: "100%",
          }}
        >
          THIS IS THE DEVELOPER WEBSITE AND MAY NOT BE WORKING
        </div>
      )}

      {/* Main content area with padding for fixed navbar and banner */}
      <div
        style={{
          paddingTop: `${
            headerHeight + (!isProductionUrl() ? bannerHeight : 0)
          }px`,
        }}
      >
        {userStore.venueChangeInProgress ? (
          // Venue switch in progress: take the page down and show a loader.
          <div
            role="status"
            aria-live="polite"
            aria-label="Switching venue"
            style={{ width: "100%" }}
          >
            <PEProgressBar />
          </div>
        ) : (
          // Keying on the venue forces a full remount of the page after a switch, so
          // component state, effects and fetches all start fresh for the new venue.
          <Fragment key={selectedVenue?.venueId ?? "no-venue"}>
            {props.children}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
});
export default DashboardLayout;
